// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@awc/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";

const enum fields {
    imageLinks = "imageLinks",
}

export interface ImageLinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        title: string;
        image: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageLinks]: ImageLinkModel[];
    };
}

export interface AlliedUnirversityLinksStyles {
    actionClickable?: ClickablePresentationProps;
    title?: TypographyPresentationProps;
    image?: LazyImageProps;
    wrapper?: InjectableCss;
}

export const alliedUnirversityLinksStyles: AlliedUnirversityLinksStyles = {};

const styles = alliedUnirversityLinksStyles;

export const AlliedUnirversityLinks: React.FC<OwnProps> = ({ fields }) => {
    const { links: imageLinks } = useGetLinks(fields.imageLinks, o => o.fields.destination);

    if (!fields.imageLinks.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {imageLinks.map((link, index) => {
                const imageLink = fields.imageLinks[index].fields;
                const shouldOpenInNewWindow = imageLink.openInNewWindow;
                const hasValidUrl = Boolean(link.url);

                if (!hasValidUrl) {
                    return null;
                }

                return (
                    <Clickable
                        {...styles.actionClickable}
                        key={`${link.url}.${link.title}`}
                        href={link.url}
                        target={shouldOpenInNewWindow ? "_blank" : ""}
                    >
                        <LazyImage {...styles.image} src={imageLink.image} />
                        {imageLink.title && <Typography {...styles.title}>{imageLink.title}</Typography>}
                    </Clickable>
                );
            })}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: AlliedUnirversityLinks,
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.imageLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    return item.fields.title || "title";
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Page Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
